/**
 * Todo Domain Schemas
 * Validation schemas for todo entities and operations
 */

import { z } from 'zod';
import { uuidSchema, dateStringSchema } from './common.schemas';

/**
 * Todo Priority Enum
 */
export const todoPrioritySchema = z.enum(['low', 'medium', 'high', 'urgent']);
export type TodoPriority = z.infer<typeof todoPrioritySchema>;

/**
 * Todo Status Enum — validation subset
 * Matches the canonical TypeScript Todo interface status: 'todo' | 'in_progress' | 'completed'.
 * The database CHECK constraint supports additional statuses ('pending', 'cancelled', 'blocked',
 * 'on_hold') for internal workflows, but the validation schema enforces the three statuses
 * that are interoperable with Google Tasks sync (needsAction ↔ todo, completed ↔ completed).
 */
export const todoStatusSchema = z.enum(['todo', 'in_progress', 'completed']);
export type TodoStatus = z.infer<typeof todoStatusSchema>;

/**
 * Base Todo Schema
 * Represents a complete todo item from the database
 */
export const todoSchema = z.object({
  id: uuidSchema,
  user_id: uuidSchema,
  title: z.string().min(1).max(500),
  description: z.string().max(5000).nullable(),
  completed: z.boolean().default(false),
  priority: todoPrioritySchema.default('medium'),
  status: todoStatusSchema.default('todo'),
  due_date: dateStringSchema.nullable(),
  completed_at: dateStringSchema.nullable(),
  category_id: uuidSchema.nullable(),
  project_id: uuidSchema.nullable(),
  tags: z.array(z.string()).default([]),
  order_index: z.number().int().nonnegative().default(0),
  estimated_minutes: z.number().int().positive().nullable(),
  actual_minutes: z.number().int().positive().nullable(),
  created_at: dateStringSchema,
  updated_at: dateStringSchema,
});

export type Todo = z.infer<typeof todoSchema>;

/**
 * Create Todo Schema
 * For creating new todos - omits auto-generated fields
 */
export const createTodoSchema = todoSchema.omit({
  id: true,
  completed_at: true,
  created_at: true,
  updated_at: true,
}).extend({
  user_id: uuidSchema.optional(), // Can be inferred from session
  title: z.string().min(1, 'Title is required').max(500, 'Title must be less than 500 characters').trim(),
  description: z.string().max(5000).optional().nullable().transform(val => val || null),
});

export type CreateTodoInput = z.infer<typeof createTodoSchema>;

/**
 * Update Todo Schema
 * For partial updates - all fields optional except id
 */
export const updateTodoSchema = todoSchema.partial().required({
  id: true,
}).omit({
  user_id: true, // Cannot change owner
  created_at: true, // Cannot change creation time
});

export type UpdateTodoInput = z.infer<typeof updateTodoSchema>;

/**
 * Complete Todo Schema
 * For marking todos as complete
 */
export const completeTodoSchema = z.object({
  id: uuidSchema,
  completed: z.literal(true),
  completed_at: dateStringSchema.optional(),
  actual_minutes: z.number().int().positive().optional(),
});

export type CompleteTodoInput = z.infer<typeof completeTodoSchema>;

/**
 * Todo Filter Schema
 * For query filters and search
 */
export const todoFilterSchema = z.object({
  completed: z.boolean().optional(),
  status: z.array(todoStatusSchema).optional(),
  priority: z.array(todoPrioritySchema).optional(),
  category_id: uuidSchema.optional(),
  project_id: uuidSchema.optional(),
  tags: z.array(z.string()).optional(),
  search: z.string().max(500).optional(),
  due_before: dateStringSchema.optional(),
  due_after: dateStringSchema.optional(),
  created_after: dateStringSchema.optional(),
  created_before: dateStringSchema.optional(),
});

export type TodoFilter = z.infer<typeof todoFilterSchema>;

/**
 * Bulk Todo Operation Schema
 */
export const bulkTodoOperationSchema = z.object({
  ids: z.array(uuidSchema).min(1, 'At least one todo ID is required'),
  operation: z.enum(['complete', 'delete', 'archive', 'update_priority', 'update_category']),
  data: z.record(z.unknown()).optional(),
});

export type BulkTodoOperation = z.infer<typeof bulkTodoOperationSchema>;

/**
 * Todo Category Schema
 */
export const todoCategorySchema = z.object({
  id: uuidSchema,
  user_id: uuidSchema,
  name: z.string().min(1).max(100),
  color: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid hex color').nullable(),
  icon: z.string().max(50).nullable(),
  order_index: z.number().int().nonnegative().default(0),
  created_at: dateStringSchema,
  updated_at: dateStringSchema,
});

export type TodoCategory = z.infer<typeof todoCategorySchema>;

/**
 * Create Todo Category Schema
 */
export const createTodoCategorySchema = todoCategorySchema.omit({
  id: true,
  created_at: true,
  updated_at: true,
}).extend({
  user_id: uuidSchema.optional(),
  name: z.string().min(1, 'Category name is required').max(100, 'Category name must be less than 100 characters').trim(),
});

export type CreateTodoCategoryInput = z.infer<typeof createTodoCategorySchema>;

/**
 * Update Todo Category Schema
 */
export const updateTodoCategorySchema = todoCategorySchema.partial().required({
  id: true,
}).omit({
  user_id: true,
  created_at: true,
});

export type UpdateTodoCategoryInput = z.infer<typeof updateTodoCategorySchema>;
